import { useEffect, useState } from "react";
import { api, formatApiError, formatBRL } from "../lib/api";
import { Target, Calendar, Gift, Check } from "lucide-react";
import { toast } from "sonner";
import { fireBigConfetti } from "../lib/confetti";
import { useAuth } from "../context/AuthContext";

export default function Missions() {
  const [missions, setMissions] = useState([]);
  const { refresh } = useAuth();

  const load = () => api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const claim = async (id) => {
    try {
      const { data } = await api.post(`/missions/${id}/claim`);
      fireBigConfetti();
      toast.success(`Recompensa resgatada! +${data.xp_awarded} XP`);
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const daily = missions.filter((m) => m.scope === "daily");
  const weekly = missions.filter((m) => m.scope === "weekly");

  const Section = ({ title, icon: Ico, items, color, testid }) => (
    <div className="mb-6">
      <h2 className="font-arcade text-lg font-bold text-white flex items-center gap-2 mb-3">
        <Ico size={18} className={color} />
        {title}
      </h2>
      <div className="space-y-3" data-testid={testid}>
        {items.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4">Nenhuma missão nesta categoria</p>
        )}
        {items.map((m) => {
          const pct = Math.min(100, (m.progress / m.target) * 100);
          return (
            <div key={m.id} className="neon-card p-4" data-testid={`mission-item-${m.scope}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-arcade text-base font-bold text-white">{m.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{m.description}</p>
                </div>
                <span className="chip chip-gold whitespace-nowrap">+{m.xp_reward} XP</span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-[11px] font-mono text-slate-400 mb-1">
                  <span>{m.target_type === "sales_value" ? formatBRL(m.progress) : m.progress}</span>
                  <span>{m.target_type === "sales_value" ? formatBRL(m.target) : m.target}</span>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
              {m.completed && !m.claimed && (
                <button
                  onClick={() => claim(m.id)}
                  data-testid={`claim-reward-btn-${m.id}`}
                  className="btn-gold w-full mt-3 flex items-center justify-center gap-2 text-sm"
                >
                  <Gift size={16} /> Resgatar recompensa
                </button>
              )}
              {m.claimed && (
                <div className="mt-3 chip chip-green justify-center w-full py-2">
                  <Check size={14} /> Recompensa resgatada
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="arcade-grid min-h-screen pb-28">
      <div className="phone-frame phone-frame-full px-4 pt-6">
        <div className="mb-5">
          <p className="chip chip-cyan mb-2">🎯 Suas missões</p>
          <h1 className="font-arcade text-3xl font-extrabold text-white tracking-tight">
            Missões & Desafios
          </h1>
          <p className="text-sm text-slate-400 mt-1">Complete objetivos e ganhe XP extra</p>
        </div>

        <Section title="Diárias" icon={Target} items={daily} color="text-fuchsia-400" testid="daily-missions-list" />
        <Section title="Semanais" icon={Calendar} items={weekly} color="text-cyan-400" testid="weekly-missions-list" />
      </div>
    </div>
  );
}
